import { useMemo } from "react";
import { useAppSelector } from "../../../../store";
import { useGetDataviewKpisQuery } from "../../../../services/Dataview/DataviewAPI";
import { getAbo, getAff, storedLocale } from "../../../Authentication/util";
import type { AboKpiData } from "../../../../services/Dataview/DataviewAPI.types";
import type { PerfIncomeKpiItemProps, KpiVolumeByCountry } from "./PerIncomeKpiItemTile/PerfIncomeKpiItemTile";

/**
 * Parses a comma-separated string into an array of trimmed strings.
 * Returns an empty array if the input is empty or undefined.
 */
const parseKpiString = (kpiString: string | undefined): string[] => {
  if (!kpiString || kpiString.trim() === "") {
    return [];
  }

  return kpiString.split(",").map((kpi) => kpi.trim());
};

/**
 * Transforms API KPI data into the format expected by PerfIncomeKpiItemTile
 */
const transformKpiData = (apiKpi: AboKpiData): PerfIncomeKpiItemProps => {
  if (apiKpi.volumeRequirement && apiKpi.name === "personalPVRequirement") {
    return {
      label: apiKpi.name,
      value: apiKpi.volumeRequirement.target?.value || 0,
      formattedValue: apiKpi.volumeRequirement.target?.formatted || "0",
      isMet: apiKpi.volumeRequirement.met,
      volumeCode: apiKpi.volumeRequirement.volume?.code,
    };
  }

  if (apiKpi.volume) {
    const volumeByCountry: KpiVolumeByCountry = {};

    if (apiKpi.volume.byCountry) {
      Object.entries(apiKpi.volume.byCountry).forEach(([countryCode, countryData]) => {
        volumeByCountry[countryCode] = {
          value: countryData.value,
          formattedValue: countryData.formatted,
        };
      });
    }

    return {
      label: apiKpi.name,
      value: apiKpi.volume.total.value,
      formattedValue: apiKpi.volume.total.formatted,
      volumeByCountry: Object.keys(volumeByCountry).length > 0 ? volumeByCountry : undefined,
      isMet: apiKpi.isMet,
    };
  }

  // Handle decimal percent KPIs (e.g. cpvPercent returns float value)
  if (apiKpi.decimalPercent) {
    return {
      label: apiKpi.name,
      value: apiKpi.decimalPercent.value,
      formattedValue: apiKpi.decimalPercent.formatted,
      isMet: apiKpi.isMet,
    };
  }

  // Handle percent-based KPIs
  if (apiKpi.percent) {
    return {
      label: apiKpi.name,
      value: apiKpi.percent.value,
      formattedValue: apiKpi.percent.formatted,
      isMet: apiKpi.isMet,
    };
  }

  if (apiKpi.intValue) {
    return {
      label: apiKpi.name,
      value: apiKpi.intValue.value,
      formattedValue: apiKpi.intValue.formatted,
      isMet: apiKpi.isMet,
    };
  }
  // Fallback for KPIs without volume or percent
  return {
    label: apiKpi.name,
    value: 0,
    formattedValue: "0",
    isMet: apiKpi.isMet,
  };
};

/**
 * Custom hook for fetching KPI configuration data from the Dataview API.
 * Follows the pattern from MonthlyPerformanceDashDataLayer.
 *
 * @param dataviewId - The identifier for the dataview to fetch (e.g., "landingPage")
 * @param period - The period in YYYYMM format
 * @returns Object containing loading state, error state, available KPIs, selected KPIs, KPI data, and refetch function
 */
export const FetchPerfIncomeKpiData = (dataviewId: string, period: string) => {
  const { configuration } = useAppSelector((state) => state.boot);
  const { isoCountryCode } = useAppSelector((state) => state.boot.user);
  const locale = storedLocale();

  const shouldSkip = !period || !dataviewId;

  const {
    data: payloadData,
    isFetching,
    isError,
    refetch,
  } = useGetDataviewKpisQuery(
    {
      baseUrl: configuration.apiBaseUrl,
      DataViewID: dataviewId,
      Abo: `${getAbo()}`,
      Aff: `${getAff()}`,
      Period: period,
      Locale: locale,
      IsoCountryCode: isoCountryCode,
    },
    { skip: shouldSkip, refetchOnMountOrArgChange: true },
  );

  const isLoading = shouldSkip || (isFetching && !payloadData);

  const { availableKpis, selectedKpis, kpis, personalPVRequirementCode } = useMemo(() => {
    const available = parseKpiString(payloadData?.AvailableKpis);
    const selected = parseKpiString(payloadData?.SelectedKpis);

    const transformedKpis: PerfIncomeKpiItemProps[] = [];
    if (payloadData?.AboKpiDataSequence?.[0]?.kpis) {
      payloadData.AboKpiDataSequence[0].kpis.forEach((apiKpi) => {
        transformedKpis.push(transformKpiData(apiKpi));
      });
    }

    return {
      availableKpis: available,
      selectedKpis: selected,
      kpis: transformedKpis,
      personalPVRequirementCode: payloadData?.PersonalPVRequirementCode,
    };
  }, [payloadData]);

  const errorStatus = isError;

  return { isLoading, isFetching, errorStatus, availableKpis, selectedKpis, kpis, personalPVRequirementCode, refetch };
};
